/* WEAVEPLACE reference mega navigation v1.4 — safe with Shopify theme-editor block updates. */
(() => {
  'use strict';

  // Keep the shared guard so this replaces earlier WEAVEPLACE reference-menu versions.
  if (window.__wpRefMegaV12) return;
  window.__wpRefMegaV12 = true;

  const mq = matchMedia('(min-width:990px)');
  const states = new WeakMap();
  const bindings = new WeakMap();
  const summaryBound = new WeakSet();
  const panelBound = new WeakSet();
  const keyBound = new WeakSet();
  let active = null;
  let leaveTimer = 0;
  let initQueued = false;
  let scrimFrame = 0;
  const hideScrim = () => {
    cancelAnimationFrame(scrimFrame);
    scrimFrame = 0;
    document.documentElement.classList.remove('wp-ref-mega-scrim-visible');
  };
  const showScrimAfterPanel = details => {
    hideScrim();
    scrimFrame = requestAnimationFrame(() => {
      scrimFrame = requestAnimationFrame(() => {
        scrimFrame = 0;
        if (active === details && details.open && mq.matches) {
          document.documentElement.classList.add('wp-ref-mega-scrim-visible');
        }
      });
    });
  };

  const isReduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const panel = details => details?.querySelector(':scope > .wp-ref-panel');

  const cancel = details => {
    const animation = states.get(details);
    if (animation) {
      animation.cancel();
      states.delete(details);
    }
  };

  const finishClose = details => {
    if (!details) return;
    details.removeAttribute('open');
    details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    panel(details)?.style.removeProperty('opacity');
    panel(details)?.style.removeProperty('visibility');

    if (active === details) active = null;
    document.documentElement.classList.toggle('wp-ref-mega-open', !!active);
    if (!active) hideScrim();
  };

  const close = (details, immediate = false) => {
    if (!details || !details.open) return;
    if (immediate) clearTimeout(leaveTimer);
    cancel(details);
    // Start dimmer fade-out at the SAME moment the panel starts closing.
    hideScrim();

    const currentPanel = panel(details);
    if (immediate || !currentPanel || isReduced() || !mq.matches) {
      finishClose(details);
      return;
    }

    currentPanel.style.opacity = '1';
    const animation = currentPanel.animate(
      [{ opacity: 1 }, { opacity: 0 }],
      { duration: 200, easing: 'ease-in' }
    );
    states.set(details, animation);
    animation.onfinish = () => {
      states.delete(details);
      finishClose(details);
    };
    animation.oncancel = () => states.delete(details);
  };

  const open = details => {
    if (!mq.matches || !details) return;

    const currentPanel = panel(details);
    if (!currentPanel) return;

    clearTimeout(leaveTimer);

    if (active === details && details.open) {
      cancel(details);
      currentPanel.style.setProperty('opacity', '1');
      currentPanel.style.setProperty('visibility', 'visible');
      showScrimAfterPanel(details);
      return;
    }

    if (active && active !== details) close(active, true);

    cancel(details);
    active = details;
    details.open = true;
    details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('wp-ref-mega-open');
    showScrimAfterPanel(details);

    currentPanel.style.visibility = 'visible';
    currentPanel.style.opacity = '1';

    if (!isReduced()) {
      const animation = currentPanel.animate(
        [{ opacity: 0 }, { opacity: 1 }],
        { duration: 300, easing: 'ease-out' }
      );
      states.set(details, animation);
      animation.onfinish = () => states.delete(details);
      animation.oncancel = () => states.delete(details);
    }
  };

  const scheduleClose = details => {
    clearTimeout(leaveTimer);
    leaveTimer = setTimeout(() => {
      const currentPanel = panel(details);
      if (
        !details.matches(':hover') &&
        !currentPanel?.matches(':hover') &&
        !details.querySelector(':focus-visible')
      ) {
        close(details);
      }
    }, 220);
  };

  const bindDetails = details => {
    const summary = details.querySelector(':scope > summary');
    const currentPanel = panel(details);
    if (!summary || !currentPanel) return;

    // Shopify may keep <details> but replace its summary/panel during a block edit.
    // Rebind when either node changes instead of relying on a permanent dataset flag.
    const previous = bindings.get(details);
    if (previous?.summary === summary && previous?.panel === currentPanel) return;
    bindings.set(details, { summary, panel: currentPanel });

    if (!summaryBound.has(summary)) {
      summaryBound.add(summary);
      summary.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'touch') open(details);
      });
      summary.addEventListener('pointerleave', () => scheduleClose(details));
      summary.addEventListener('click', event => {
        // The label is a real link; preserve its navigation behavior.
        if (event.target.closest('a.wp-nav-headlink')) {
          event.stopPropagation();
          return;
        }
        if (!mq.matches) return;

        event.preventDefault();
        event.stopPropagation();
        const url = summary.querySelector('a.wp-nav-headlink')?.href;
        if (url) {
          window.location.assign(url);
        } else {
          details.open ? close(details, true) : open(details);
        }
      });
      summary.addEventListener('focus', () => open(details));
    }

    if (!panelBound.has(currentPanel)) {
      panelBound.add(currentPanel);
      currentPanel.addEventListener('pointerenter', () => clearTimeout(leaveTimer));
      currentPanel.addEventListener('pointerleave', () => scheduleClose(details));
    }

    if (details.open) {
      currentPanel.style.visibility = 'visible';
      currentPanel.style.opacity = '1';
      active = details;
      document.documentElement.classList.add('wp-ref-mega-open');
      document.documentElement.classList.add('wp-ref-mega-scrim-visible');
    } else if (active === details) {
      active = null;
      document.documentElement.classList.remove('wp-ref-mega-open');
      hideScrim();
    }

    if (!keyBound.has(details)) {
      keyBound.add(details);
      details.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          close(details, true);
          details.querySelector(':scope > summary')?.focus();
        }
      });
    }
  };

  const init = () => {
    document.querySelectorAll('.wp-ref-nav').forEach(nav => {
      nav.querySelectorAll('header-megamenu > details').forEach(bindDetails);

      if (!nav.dataset.refFocusBound14) {
        nav.dataset.refFocusBound14 = 'true';
        nav.addEventListener('focusout', () => {
          setTimeout(() => {
            if (active && !nav.contains(document.activeElement)) scheduleClose(active);
          }, 0);
        });
      }
    });
  };

  document.addEventListener('pointerdown', event => {
    if (active && !active.contains(event.target)) close(active, true);
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && active) close(active, true);
  });
  mq.addEventListener('change', () => {
    if (active) close(active, true);
  });

  const queueInit = () => {
    if (initQueued) return;
    initQueued = true;
    Promise.resolve().then(() => {
      initQueued = false;
      init();
    });
  };

  const editorObserver = new MutationObserver(records => {
    let shouldInit = false;

    for (const record of records) {
      const target = record.target;
      if (target.nodeType === 1 && (target.matches('.wp-ref-nav') || target.closest('.wp-ref-nav'))) {
        shouldInit = true;
      }
      if (!shouldInit) {
        for (const node of record.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (
            node.matches('.wp-ref-nav, header-megamenu, header-megamenu > details') ||
            node.querySelector('.wp-ref-nav, header-megamenu, header-megamenu > details')
          ) {
            shouldInit = true;
            break;
          }
        }
      }
      if (shouldInit) break;
    }

    if (active && !document.documentElement.contains(active)) {
      active = null;
      document.documentElement.classList.remove('wp-ref-mega-open');
      hideScrim();
    }

    if (shouldInit) queueInit();
  });

  editorObserver.observe(document.documentElement, { childList: true, subtree: true });
  // A cached navigation can restore old HTML classes without an open menu.
  const syncScrim = () => {
    if (active && (!active.isConnected || !active.open || !mq.matches)) {
      cancel(active);
      active = null;
    }
    if (!active) {
      document.documentElement.classList.remove('wp-ref-mega-open', 'wp-ref-mega-scrim-visible');
    }
  };
  window.addEventListener('pageshow', () => {
    syncScrim();
    queueInit();
  });
  window.addEventListener('pagehide', () => {
    clearTimeout(leaveTimer);
    if (active) close(active, true);
    document.documentElement.classList.remove('wp-ref-mega-open', 'wp-ref-mega-scrim-visible');
  });

  document.addEventListener('shopify:section:load', queueInit);
  document.addEventListener('shopify:block:select', queueInit);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
